// ─────────────────────────────────────────────────────────────────────────────
// VENDORED from the marketing site (magic-motorsports repo root, vehicleNames.js).
//
// The marketing site imports this from BOTH its Vite frontend and server.js so
// that a card, the detail page and the prerendered bot HTML all print the same
// vehicle name. The auction portal now renders the same dealer-stock listings,
// so it has to agree with them too — a Skyline that reads "Skyline R33 GT-R" on
// importjdmmagic.com must not read "Skyline" here.
//
// Kept as a VERBATIM copy rather than a rewrite: the two files are
// dependency-free ESM, and any divergence would show up as two different names
// for one car. If you change the marketing-site original, re-copy it here.
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Make/model name normalization for the scraped `carlistings` feed.
 *
 * The auction scraper stores names exactly as the Japanese source wrote them,
 * which produces three problems for SEO:
 *
 *   1. Untranslated Japanese      "Land Cruiserプラド", "Crownマジェスタ"
 *   2. Full-width characters      "Land Cruiser８０", "ＧＲCorolla", "ＢＭＷ"
 *   3. Inconsistent casing        "Silvia" and "silvia" as separate models
 *
 * createSlugServer() strips every non-ASCII character, so all three collapse
 * into wrong URLs. Seven Land Cruiser generations became one /land-cruiser
 * page; 54 Japanese-named makes slugged to the empty string and emitted
 * /makes//condor. Normalizing first means "Land Cruiser８０" and
 * "Land Cruiser 80" resolve to the same canonical page instead of competing.
 *
 * Both maps are intentionally data, not heuristics — a transliteration guess
 * that silently renames a real model is worse than leaving it alone. Anything
 * not in the maps passes through untouched.
 */

// Full-width ASCII (U+FF01–U+FF5E) maps onto ASCII by subtracting 0xFEE0.
// Covers ８０ -> 80, ＧＲ -> GR, Ｂ４ -> B4, ＴＴ -> TT in one pass.
export const normalizeFullWidth = (str) =>
  String(str || '')
    .replace(/[！-～]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xFEE0))
    // U+3000 ideographic space, written as an escape so it is visible in a diff
    // — matches how vehicleTitle.js spells it.
    .replace(/\u3000/g, ' ');

// Japanese make names, as stored in the feed. Counts are cars at time of audit.
const JP_MAKES = {
  '三菱ふそう': 'Mitsubishi Fuso',
  'ユーノス': 'Eunos',
  'ローバー': 'Rover',
  '日野自動車': 'Hino',
  '日野': 'Hino',
  'ダッジ': 'Dodge',
  'クライスラー・ジープ': 'Chrysler Jeep',
  'クライスラー': 'Chrysler',
  '日本その他': 'Other (Japan)',
  'ミツオカ': 'Mitsuoka',
  'ミニ': 'Mini',
  'リンカーン': 'Lincoln',
  // Match the "(USA)" form the feed already uses for these in English, so the
  // Japanese and English rows fold together instead of making two pages.
  '米国トヨタ': 'Toyota (USA)',
  '米国日産': 'Nissan (USA)',
  'アメリカホンダ': 'Honda (USA)',
  'アメリカその他': 'Other (USA)',
  'デイムラー': 'Daimler',
  'ポンテアック': 'Pontiac',
  'ロールスロイス': 'Rolls-Royce',
  'ケータハム': 'Caterham',
  'ビュイック': 'Buick',
  'マセラティ': 'Maserati',
  'マーキュリー': 'Mercury',
  'オースチン': 'Austin',
  '日産ディーゼル': 'Nissan Diesel',
  'サーブ': 'Saab',
  'モーク': 'Moke',
  'スマート': 'Smart',
  'モーリス': 'Morris',
  'プリムス': 'Plymouth',
  'ヒーレー': 'Healey',
  'イギリスその他': 'Other (UK)',
  'ドイツその他': 'Other (Germany)',
  'その他': 'Other',
  'トライアンフ': 'Triumph',
  'ランチア': 'Lancia',
  'ランボルギーニ': 'Lamborghini',
  'モーガン': 'Morgan',
  'スタークラフト': 'Starcraft',
  'バーキン': 'Birkin',
  'カーボディーズ': 'Carbodies',
  'パンサー': 'Panther',
  'ハマー': 'Hummer',
  'アウトビアンキ': 'Autobianchi',
  'インフィニティ': 'Infiniti',
  'オペル': 'Opel',
  'アルファ ロメオ': 'Alfa Romeo',
  'アルファロメオ': 'Alfa Romeo',
  '光岡自動車': 'Mitsuoka',
  '国産車その他': 'Other (Japan)',
  '輸入車その他': 'Other (Imported)',
  // Mixed full-width + katakana. Keyed on the post-normalizeFullWidth form as
  // well, since that is what the lookup sees on the second pass. Without these
  // "ＢＭＷアルピナ" slugs to "bmw" and silently merges into the BMW page.
  'ＢＭＷアルピナ': 'BMW Alpina',
  'BMWアルピナ': 'BMW Alpina',
  'ＡＭＣ・ジープ': 'AMC Jeep',
  'AMC・ジープ': 'AMC Jeep',
  'ＵＤトラックス': 'UD Trucks',
  'UDトラックス': 'UD Trucks',
};

// Model-name fragments. Applied as substring replacements because the feed
// writes these as an English base plus a Japanese suffix ("Civicフェリオ").
// Longest-first at call time so "ツーリングワゴン" wins over "ワゴン".
const JP_MODEL_TERMS = {
  'プラド': 'Prado',
  'マジェスタ': 'Majesta',
  'ステーションワゴン': 'Station Wagon',
  'ツーリングワゴン': 'Touring Wagon',
  'スポーツワゴン': 'Sports Wagon',
  'スポーツピック': 'Sports Pickup',
  'ピックアップ': 'Pickup',
  'エアロデッキ': 'Aerodeck',
  'ランカスター': 'Lancaster',
  'ハイブリッド': 'Hybrid',
  'エステート': 'Estate',
  'ツーリング': 'Touring',
  'スパシオ': 'Spacio',
  'フェリオ': 'Ferio',
  'シャトル': 'Shuttle',
  'ブリット': 'Blit',
  'クオリス': 'Qualis',
  'ランクス': 'Runx',
  'スポーツ': 'Sports',
  'ホーミー': 'Homy',
  'レビン': 'Levin',
  'セレス': 'Ceres',
  'ジータ': 'Gita',
  'クーペ': 'Coupe',
  'ワゴン': 'Wagon',
  'バン': 'Van',
  'トレノ': 'Trueno',
  'サーフ': 'Surf',
  'グランデ': 'Grande',
  // Make names the feed sometimes parks at the FRONT of the model field
  // ("ユーノスRoadster", "リンカーン Mark III"). JP_MAKES only matches a whole
  // make string, so without these the katakana survives into the title and H1,
  // and — because createSlugServer strips it — the same car ends up on two
  // indexable pages: /makes/eunos/roadster and /makes/eunos/eunos-roadster.
  'ユーノス': 'Eunos',
  'リンカーン': 'Lincoln',
  'ホンダ': 'Honda',
  'トヨタ': 'Toyota',
  'ニッサン': 'Nissan',
  'マツダ': 'Mazda',
  'スバル': 'Subaru',
  'ミツビシ': 'Mitsubishi',
  'スズキ': 'Suzuki',
  'ダイハツ': 'Daihatsu',
  'タイプR': 'Type R',
  'タイプ R': 'Type R',
  // The scraper's catch-all bucket. Carries no meaning in a model name and
  // reads as noise in a title, so it is dropped rather than transliterated.
  'その他': '',
};

const JP_TERMS_SORTED = Object.keys(JP_MODEL_TERMS).sort((a, b) => b.length - a.length);

/**
 * Nameplates the manufacturer writes closed-up.
 *
 * The CamelCase rules in tidySpacing are needed to fold the feed's two
 * spellings of the Ace vans ("TownAce Truck" and "Town Ace Truck" both occur),
 * but they split these names too — turning ~210 Toyota HiAce into "Hi Ace" in
 * the title, H1, slug and sitemap, while "Grand Hiace" sat untouched beside it.
 *
 * Re-joining afterwards fixes that and does the folding at the same time: every
 * spelling and casing the feed uses ("HiAce", "Hi Ace", "Hiace", "hiace")
 * converges on the manufacturer's own form. \s* rather than a literal space so
 * the already-closed-up spelling is matched and re-cased too.
 */
const CLOSED_UP_NAMES = [
  [/\bhi\s*ace\b/gi, 'HiAce'],
  [/\btown\s*ace\b/gi, 'TownAce'],
  [/\blite\s*ace\b/gi, 'LiteAce'],
  [/\btoyo\s*ace\b/gi, 'ToyoAce'],
  [/\bmaster\s*ace\b/gi, 'MasterAce'],
  [/\bvehi\s*cross\b/gi, 'VehiCross'],
  [/\bde\s*ville\b/gi, 'DeVille'],
  [/\bla\s*crosse\b/gi, 'LaCrosse'],
  [/\bstar\s*craft\b/gi, 'StarCraft'],
  // Digit-led nameplate: the Z3Roadster rule below splits this too, and unlike
  // "Z3 Roadster" the manufacturer really does write it closed-up.
  [/\b4\s*runner\b/gi, '4Runner'],
];

const restoreClosedUpNames = (s) =>
  CLOSED_UP_NAMES.reduce((out, [re, name]) => out.replace(re, name), s);

const tidySpacing = (s) =>
  restoreClosedUpNames(
    s.replace(/\s+/g, ' ')
      // "Land Cruiser80" -> "Land Cruiser 80"; "CorollaFX" -> "Corolla FX"
      .replace(/([a-z])(\d)/g, '$1 $2')
      .replace(/([a-zA-Z])([A-Z][a-z])/g, '$1 $2')
      // "LegacyB4" -> "Legacy B4", "CorollaII" -> "Corolla II". Requires two or
      // more lowercase before the capital so the Toyota "bB" survives intact.
      .replace(/([a-z]{2,})([A-Z])/g, '$1 $2')
      // "Z3Roadster" -> "Z3 Roadster". Digit-then-capitalised-word is
      // unreachable by the rules above (the char before the capital is a digit,
      // and the capital is not preceded by a letter), so this was the one
      // survivor of the nameplate work. Requires a following lowercase, which
      // is what keeps "4Runner"... deliberately split, but leaves "Z4M",
      // "RAV4 J" and "MR2" untouched.
      .replace(/(\d)([A-Z][a-z])/g, '$1 $2')
  ).trim();

export const normalizeMakeName = (make) => {
  const raw = String(make || '').trim();
  if (!raw) return '';
  if (JP_MAKES[raw]) return JP_MAKES[raw];
  const widened = normalizeFullWidth(raw).trim();
  return JP_MAKES[widened] || widened;
};

export const normalizeModelName = (model) => {
  let out = normalizeFullWidth(String(model || '').trim());
  for (const jp of JP_TERMS_SORTED) {
    if (out.includes(jp)) out = out.split(jp).join(` ${JP_MODEL_TERMS[jp]} `);
  }
  return tidySpacing(out);
};

/**
 * Transmission, which the feed stores in Japanese on ~20,500 active listings:
 * "フロア6MT", "AT5速", "コラムAT", "MTモード付きAT". That text was reaching the
 * listing cards, the model-page spec lines and the /dealer-stock prerender, so
 * a crawler read "2002 Nissan Skyline GT-R R34 - 14k km, フロア6MT".
 *
 * Parsed rather than enumerated — there are 59 distinct values today and the
 * scraper invents new combinations, so a lookup table would silently miss them.
 * The shift position is kept because enthusiasts care (a column-shift auto is a
 * very different car from a floor-shift manual).
 */
const SHIFT_POSITIONS = [
  [/フロア/, 'floor'],
  [/コラム/, 'column'],
  [/インパネ/, 'dash'],
];

export const normalizeTransmission = (value) => {
  let s = normalizeFullWidth(String(value || '').trim());
  if (!s || s === '-') return '';
  // Junk the scraper parks in this field.
  if (/オプション|保証/.test(s)) return '';

  let position = '';
  for (const [re, label] of SHIFT_POSITIONS) {
    if (re.test(s)) { position = label; s = s.replace(re, ''); break; }
  }
  const manualMode = /MTモード付き?/.test(s);
  s = s.replace(/MTモード付き?/, '');
  s = s.replace(/その他/, '');

  // "5速" (5-speed) or the "5MT" form where the digit leads the type.
  const speed = (s.match(/(\d+)\s*速/) || s.match(/(\d+)\s*(?=MT|AT|CVT)/) || [])[1] || '';
  const type = /CVT/.test(s) ? 'CVT' : /MT/.test(s) ? 'MT' : /AT/.test(s) ? 'AT' : '';
  if (!type) return position ? '' : s.replace(/[\u3000-\u9fff\uff00-\uffef]/g, '').trim();

  return [
    speed ? `${speed}-speed ${type}` : type,
    manualMode ? ' with manual mode' : '',
    position ? ` (${position} shift)` : '',
  ].join('');
};

/**
 * Japanese body type -> the English label we publish.
 *
 * This lives here, beside normalizeTransmission, because BOTH tiers must agree:
 * the prerenderer writes this string into <title>, the meta description, the
 * JSON-LD and anchor text, and the SPA renders it on the same URL. When the two
 * disagreed, a crawler saw "Coupe" where a browser showed
 * "Coupe / Sports car (クーペ・スポーツ)" — same URL, different content, which
 * is what Google treats as cloaking. One function, one answer.
 *
 * Compound and kei keys are listed first: matching is by `includes()`, so
 * "軽-トラック" has to be tested before the bare "トラック" it contains.
 */
const BODY_TYPES = {
  '軽-rv系': 'Kei Car', '軽-キャブバン': 'Kei Van', '軽-トラック': 'Kei Truck', '軽自動車': 'Kei Car',
  'オープン・カブリオレ': 'Convertible', 'クーペ・スポーツ': 'Coupe', 'ミニバン・ワンボックス': 'Minivan',
  'suv・クロスカントリー': 'SUV', 'ピックアップトラック': 'Pickup', 'キャブバン': 'Van',
  'セダン': 'Sedan', '4door sedan': 'Sedan', 'クーペ': 'Coupe', 'coupe': 'Coupe',
  'ハッチバック': 'Hatchback', 'hatchback': 'Hatchback', 'ワゴン': 'Wagon', 'wagon': 'Wagon',
  'suv': 'SUV', 'トラック': 'Truck', 'truck': 'Truck', 'ミニバン': 'Minivan', 'minivan': 'Minivan',
  'コンバーチブル': 'Convertible', 'convertible': 'Convertible', 'ピックアップ': 'Pickup', 'pickup truck': 'Pickup',
  'スポーツカー': 'Sports Car', 'sports car': 'Sports Car', 'ハードトップ': 'Hardtop', 'hardtop': 'Hardtop',
  'ボンネットバン': 'Van', 'bonnet van': 'Van', 'バス': 'Bus', 'bus': 'Bus',
  'one-box': 'Van', 'kei car': 'Kei Car', 'kei truck': 'Kei Truck',
};

/**
 * Returns '' — never 'N/A' and never raw Japanese — when there is nothing
 * publishable. Callers interpolate this into titles and anchor text, so an
 * empty string has to stay falsy; the previous SPA version returned the
 * truthy literal 'N/A', which defeated every `|| ''` guard downstream.
 */
export const translateBodyType = (bodyType) => {
  if (!bodyType) return '';
  const lower = String(bodyType).toLowerCase();
  for (const [key, val] of Object.entries(BODY_TYPES)) {
    if (lower.includes(key)) return val;
  }
  // "その他" is the feed's catch-all, on 221 active listings. It carries no
  // information and rendered as raw Japanese in the spec line, so drop it
  // rather than print "Other".
  if (/^(その他|other)$/i.test(String(bodyType).trim())) return '';
  return bodyType;
};

/**
 * Grouping key. Not for display.
 *
 * Separator-insensitive as well as case-insensitive, because the feed spells
 * one nameplate several ways and only collapsing whitespace left duplicates
 * standing: minicab-truck(166) beside mini-cab-truck(22), bighorn(23) beside
 * big-horn(5), z3-roadster(45) beside z3roadster(11) — each pair two
 * self-canonical indexable pages for the same car, splitting its own inventory.
 */
export const nameKey = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * Pick the display name for a set of variants that share a key.
 *
 * Capitalization outranks count. Sorting by count first made the tie-break
 * unreachable in the only cases it mattered — the scraper's all-lowercase
 * spelling is often the more numerous one, so "super great"(23) beat
 * "Super Great"(14) and shipped as the <h1> of an indexable page, directly
 * above listing cards that read "Super Great" because those come from
 * buildVehicleTitle. Any variant carrying a capital now wins over one carrying
 * none; count only decides between two properly-cased spellings, which is what
 * keeps "HiAce Wagon"(124) ahead of "Hiace Wagon"(2).
 */
const isAllLowercase = (name) => name === name.toLowerCase() && /[a-z]/.test(name);

const pickDisplayName = (variants) => {
  const scored = variants.map((v) => ({
    name: v.name,
    count: v.count || 0,
    cased: isAllLowercase(v.name) ? 0 : 1,
    caps: (v.name.match(/[A-Z]/g) || []).length,
  }));
  scored.sort((a, b) =>
    b.cased - a.cased || b.count - a.count || b.caps - a.caps || a.name.localeCompare(b.name));
  const winner = scored[0].name;
  // 58 groups have no cased variant at all — the scraper wrote every spelling
  // in lowercase ("dynatrack", "cyanos"). Nothing to prefer, so title-case for
  // display; an all-lowercase <h1> reads as broken. Matches capitalizeModel in
  // vehicleTitle.js, so the heading and the listing cards below it agree.
  return isAllLowercase(winner)
    ? winner.replace(/\b[a-z]/g, (c) => c.toUpperCase())
    : winner;
};

/**
 * Fold raw rows into canonical entries.
 *
 * Two passes. The first groups by normalized display name; the second regroups
 * by slug. The second pass is what actually guarantees "one URL = one page":
 * slugify strips characters (N-BOX and N-BOX+ both become "n-box"), so distinct
 * display names can still land on the same URL. Enforcing the invariant here
 * rather than in the mapping tables means no future scraper output can quietly
 * reintroduce a collision.
 *
 * `variants` lists every raw string that folded in — callers need those to
 * query Mongo, which still holds the unnormalized values.
 */
const foldRows = (rows, { field, normalize, slugify }) => {
  const byName = new Map();
  for (const row of rows) {
    const raw = row[field] ?? row._id ?? '';
    if (!String(raw).trim()) continue;
    const display = normalize(raw);
    if (!display) continue;
    const key = nameKey(display);
    if (!byName.has(key)) byName.set(key, { names: [], count: 0, variants: [] });
    const g = byName.get(key);
    g.names.push({ name: display, count: row.count || 0 });
    g.count += row.count || 0;
    g.variants.push(raw);
  }

  const bySlug = new Map();
  for (const g of byName.values()) {
    const display = pickDisplayName(g.names);
    const slug = slugify(display);
    if (!slug) continue;
    if (!bySlug.has(slug)) bySlug.set(slug, { names: [], count: 0, variants: [] });
    const s = bySlug.get(slug);
    s.names.push({ name: display, count: g.count });
    s.count += g.count;
    s.variants.push(...g.variants);
  }

  return [...bySlug.entries()]
    .map(([slug, s]) => ({
      [field]: pickDisplayName(s.names),
      count: s.count,
      slug,
      variants: s.variants,
    }))
    .sort((a, b) => b.count - a.count || String(a[field]).localeCompare(String(b[field])));
};

export const mergeModelGroups = (rows, slugify) =>
  foldRows(rows, { field: 'model', normalize: normalizeModelName, slugify });

export const mergeMakeGroups = (rows, slugify) =>
  foldRows(rows, { field: 'make', normalize: normalizeMakeName, slugify });

/**
 * Minimum cars for a model page to be worth indexing. Below this the page is a
 * heading and one or two thumbnails, and dealer stock churns — a 1-car page is
 * an empty page as soon as that car sells. Such pages still render and still
 * work for visitors; they just carry noindex and stay out of the sitemap.
 */
export const MIN_INDEXABLE_MODEL_COUNT = 5;
